import React, { useEffect } from 'react';
import { RootState, useAppSelector } from '../../../../../store';
import useOpenApiTesting from '../../../../../Hooks/useOpenApiTesting';
import { ConfigurationSection, ParametersSection, TestControls, TestResults } from './components';
import { Badge } from '../../../../Utility/Badge';

type OpenApiTesterProps = {
    resetSignal: boolean;
    setResetFlag: (value: boolean) => void;
    preselectedConfigIdentifier?: string;
};

const OpenApiTester: React.FC<OpenApiTesterProps> = ({ resetSignal, setResetFlag, preselectedConfigIdentifier }) => {
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const [responseOpen, setResponseOpen] = React.useState(true);
    const [headersOpen, setHeadersOpen] = React.useState(false);
    const [requestOpen, setRequestOpen] = React.useState(true);
    const {
        configs,
        selectedConfig,
        endpoints,
        selectedEndpoint,
        parameters,
        testResult,
        isLoading,
        isTesting,
        handleConfigChange,
        handleEndpointChange,
        handleParameterChange,
        executeTest,
        clearResults,
        copied,
        copyToClipboard,
    } = useOpenApiTesting(resetSignal, setResetFlag);

    useEffect(() => {
        if (resetSignal) {
            clearResults();
        }
    }, [resetSignal, clearResults]);

    useEffect(() => {
        if (!preselectedConfigIdentifier || !configs || configs.length === 0) return;

        if (selectedConfig?.identifier !== preselectedConfigIdentifier) {
            handleConfigChange(preselectedConfigIdentifier);
        }
    }, [preselectedConfigIdentifier, configs, selectedConfig, handleConfigChange]);

    return (
        <div>
            {/* Header */}

            <div className="my-4">
                <h4>{i18n.translate({ id: 'testing.title', sourceName: 'OpenAPI' })}</h4>

                <>
                    {preselectedConfigIdentifier === 'new' ? (
                        <>
                            <Badge className="mt-10" variant="info">
                                <div>
                                    <p>
                                        Bitte füllen Sie mindestens die folgenden Felder aus und speichern Sie die
                                        Konfiguration, um sie anschließend hier zu testen.
                                    </p>
                                    <ul className="p mb-0 py-0">
                                        <li>Titel</li>
                                        <li>Authentifizierung</li>
                                        <li>Spezifikation</li>
                                    </ul>
                                </div>
                            </Badge>
                        </>
                    ) : (
                        <>
                            <span className="block my-2 text-md text-gray-500">
                                {i18n.translate({ id: 'testing.subtitle', sourceName: 'OpenAPI' })}
                            </span>
                        </>
                    )}
                </>
            </div>

            <div className="flex flex-col gap-6">
                {/* Configuration */}
                <ConfigurationSection
                    configs={configs}
                    selectedConfig={selectedConfig}
                    endpoints={endpoints}
                    selectedEndpoint={selectedEndpoint}
                    isLoading={isLoading}
                    onConfigChange={handleConfigChange}
                    onEndpointChange={handleEndpointChange}
                />
                {/* Parameters */}
                <ParametersSection
                    selectedEndpoint={selectedEndpoint}
                    parameters={parameters}
                    onParameterChange={handleParameterChange}
                />
                {/* Border to separate Test Results */}
                {testResult && <hr className="border-t border-layout-borderColor mt-0 mb-4"></hr>} {/* Results */}
                {/* Test Results */}
                <TestResults
                    testResult={testResult}
                    copied={copied}
                    onCopy={copyToClipboard}
                    responseOpen={responseOpen}
                    setResponseOpen={setResponseOpen}
                    headersOpen={headersOpen}
                    setHeadersOpen={setHeadersOpen}
                    requestOpen={requestOpen}
                    setRequestOpen={setRequestOpen}
                />
                {/* Test Controls */}
                {selectedConfig && selectedEndpoint && (
                    <TestControls
                        selectedEndpoint={selectedEndpoint}
                        isTesting={isTesting}
                        testResult={testResult}
                        onExecuteTest={executeTest}
                        onClearResults={clearResults}
                    />
                )}
            </div>
        </div>
    );
};

export default OpenApiTester;
